import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

import { Avatar } from '../internals/Avatar';
import {
  CHIP_CLASS,
  ICON_CLASS,
  ICON_SM_CLASS,
  QUIET_TEXT_CLASS,
  sizingClass,
} from '../tokens';

import { ICON_GLYPH } from './Icon';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

export const Chip: ComponentFn<typeof catalog, 'Chip'> = ({ props }) => {
  if (!props.text) return null;
  const state = props.appearance === 'state';
  const branch = props.appearance === 'branch';
  const Glyph = props.icon ? ICON_GLYPH[props.icon] : undefined;
  const title = props.label ? `${props.label}\n${props.text}` : props.text;
  if (props.appearance === 'quiet')
    return (
      <span
        title={title}
        className={cn(
          QUIET_TEXT_CLASS,
          'self-start',
          sizingClass(props.sizing)
        )}
      >
        {Glyph ? (
          <span
            aria-hidden="true"
            className="flex shrink-0"
            style={props.color ? { color: props.color } : undefined}
          >
            <Glyph className={ICON_SM_CLASS} />
          </span>
        ) : null}
        <span className={cn('min-w-0 truncate', props.mono && 'font-mono')}>
          {props.text}
        </span>
      </span>
    );
  return (
    <Badge
      variant="secondary"
      style={state ? { backgroundColor: props.color } : undefined}
      // A chip is one line that ends in an ellipsis, with its full text as the tooltip,
      // under the field it names when it has one
      title={title}
      className={cn(
        CHIP_CLASS,
        'self-start w-fit max-w-full',
        state &&
          'h-[calc(20*var(--ac-u))] min-h-[calc(20*var(--ac-u))] rounded-full gap-[var(--ac-gap-xs)] px-[calc(7*var(--ac-u))] border-transparent text-[length:calc(11*var(--ac-u))] font-semibold text-white hover:text-white',
        branch &&
          'h-auto min-h-[calc(24*var(--ac-u))] rounded-[calc(5*var(--ac-u))] border-transparent bg-secondary px-[calc(6*var(--ac-u))] text-[length:calc(11.5*var(--ac-u))] text-foreground-subtle font-mono',
        sizingClass(props.sizing),
        props.truncate && 'min-w-0'
      )}
    >
      {/* A colour tints the icon when there is one, as a picked priority tints its
          flag; otherwise it is a dot */}
      {props.color && !Glyph && !state ? (
        <span
          aria-hidden="true"
          className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full"
          style={{ backgroundColor: props.color }}
        />
      ) : null}
      {Glyph ? (
        <span
          className="flex shrink-0"
          style={props.color ? { color: props.color } : undefined}
        >
          <Glyph className={ICON_CLASS} />
        </span>
      ) : null}
      {props.avatarName ? (
        <Avatar
          id={props.avatarName}
          name={props.avatarName}
          avatarUrl={props.avatarUrl}
          shape="circle"
          size={16}
        />
      ) : null}
      <span className={cn('min-w-0 truncate', props.mono && 'font-mono')}>
        {props.text}
      </span>
    </Badge>
  );
};
